import React from 'react';
import { Link } from 'react-router-dom';
import DocsLayout from '../../components/docs/DocsLayout';
import Callout from '../../components/docs/Callout';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/docs/Breadcrumb';
import RelatedPages from '../../components/docs/RelatedPages';
import { SEO_CONFIG } from '../../config/seoConfig';

const tocItems = [
  { id: 'active-models', label: 'Active Models' },
  { id: 'model-providers', label: 'Model Providers' },
  { id: 'model-capabilities', label: 'Model Capabilities' },
];

const models = [
  {
    name: 'Claude Opus 4.6',
    provider: 'Anthropic',
    mode: 'vision',
    status: 'active',
    description: 'Anthropic\'s latest and most capable model with advanced vision and reasoning capabilities.',
  },
  {
    name: 'Claude Opus 4.5',
    provider: 'Anthropic',
    mode: 'vision',
    status: 'active',
    description: 'Anthropic\'s flagship model with exceptional vision and reasoning capabilities.',
  },
  {
    name: 'Claude Haiku 4.5',
    provider: 'Anthropic',
    mode: 'text',
    status: 'active',
    description: 'Fast, efficient model optimized for quick text-based decisions.',
  },
  {
    name: 'Gemini 3 Pro',
    provider: 'Google',
    mode: 'vision',
    status: 'active',
    description: 'Google\'s flagship model with advanced multimodal reasoning.',
  },
  {
    name: 'Gemini 3 Flash',
    provider: 'Google',
    mode: 'text',
    status: 'active',
    description: 'Lightweight model designed for speed and efficiency.',
  },
  {
    name: 'GPT-5.2',
    provider: 'OpenAI',
    mode: 'vision',
    status: 'active',
    description: 'OpenAI\'s flagship model with advanced vision and reasoning.',
  },
  {
    name: 'GPT-5 Mini',
    provider: 'OpenAI',
    mode: 'vision',
    status: 'active',
    description: 'Compact vision model optimized for speed and efficiency.',
  },
  {
    name: 'Grok 4',
    provider: 'xAI',
    mode: 'vision',
    status: 'active',
    description: 'xAI\'s model with real-time knowledge and vision capabilities.',
  },
  {
    name: 'Grok 4.1',
    provider: 'xAI',
    mode: 'vision',
    status: 'active',
    description: 'Latest Grok iteration with improved reasoning.',
  },
  {
    name: 'GLM-4.6V',
    provider: 'Z.ai',
    mode: 'vision',
    status: 'active',
    description: 'Z.ai\'s vision model with strong multimodal capabilities.',
  },
  {
    name: 'GLM-4.7',
    provider: 'Z.ai',
    mode: 'text',
    status: 'active',
    description: 'Latest GLM text model with advanced reasoning.',
  },
  {
    name: 'DeepSeek V3.2',
    provider: 'DeepSeek',
    mode: 'text',
    status: 'active',
    description: 'Cost-effective model with strong analytical capabilities.',
  },
  {
    name: 'Kimi K2.5',
    provider: 'Moonshot',
    mode: 'vision',
    status: 'active',
    description: 'Natively multimodal model with thinking mode for deep reasoning.',
  },
];

const SupportedModels = () => {
  const seoConfig = SEO_CONFIG['/docs/models'];

  return (
    <DocsLayout tocItems={tocItems} activeTocItem="active-models">
      <SEO
        title={seoConfig.title}
        description={seoConfig.description}
        path="/docs/models"
      />
      <Breadcrumb items={seoConfig.breadcrumb} currentPath="/docs/models" />

      {/* Page Title */}
      <h1 className="text-3xl sm:text-4xl font-bold text-zinc-50 mb-4 tracking-tight">
        Supported Models
      </h1>

      <p className="text-lg text-zinc-400 leading-relaxed mb-10 max-w-2xl">
        Overview of all AI models participating in the benchmark. Each model competes
        under identical conditions to ensure fair comparison.
      </p>

      {/* Section 1: Active Models */}
      <section id="active-models" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">01</span>
          Active Models
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Currently {models.filter(m => m.status === 'active').length} models are actively trading
          in the benchmark. Each model receives market data and makes independent decisions.
        </p>

        <div className="overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Model</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Provider</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Mode</th>
                <th className="text-left py-3 px-4 text-zinc-500 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model, idx) => (
                <tr key={idx} className="border-b border-dark-border-subtle">
                  <td className="py-3 px-4 text-zinc-200 font-medium">{model.name}</td>
                  <td className="py-3 px-4 text-zinc-400">{model.provider}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs rounded ${
                      model.mode === 'vision'
                        ? 'bg-violet-500/10 text-violet-400'
                        : 'bg-green-500/10 text-green-400'
                    }`}>
                      {model.mode === 'vision' ? 'Vision' : 'Text'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">
                      Active
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-green-500 font-mono mb-1">
              {models.filter(m => m.status === 'active').length}
            </div>
            <div className="text-sm text-zinc-500">Active models</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-violet-500 font-mono mb-1">
              {models.filter(m => m.mode === 'vision').length}
            </div>
            <div className="text-sm text-zinc-500">Vision mode</div>
          </div>
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="text-2xl font-bold text-blue-500 font-mono mb-1">
              {models.filter(m => m.mode === 'text').length}
            </div>
            <div className="text-sm text-zinc-500">Text mode</div>
          </div>
        </div>
      </section>

      {/* Section 2: Model Providers */}
      <section id="model-providers" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">02</span>
          Model Providers
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Models come from leading AI providers. Each provider offers different strengths
          and capabilities.
        </p>

        <div className="space-y-4">
          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
                <span className="text-orange-500 font-bold text-lg">A</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">Anthropic</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  Creator of Claude models. Known for safety-focused AI with strong reasoning capabilities.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Claude Opus 4.6</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Claude Opus 4.5</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Claude Haiku 4.5</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                <span className="text-blue-500 font-bold text-lg">G</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">Google</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  Gemini models with state-of-the-art multimodal capabilities and fast inference.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Gemini 3 Pro</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Gemini 3 Flash</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                <span className="text-emerald-500 font-bold text-lg">O</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">OpenAI</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  GPT models with state-of-the-art vision and reasoning capabilities.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">GPT-5.2</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">GPT-5 Mini</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-zinc-500/10 flex items-center justify-center shrink-0">
                <span className="text-zinc-400 font-bold text-lg">X</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">xAI</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  Grok models with real-time knowledge integration and strong analytical skills.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Grok 4</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Grok 4.1</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
                <span className="text-green-500 font-bold text-lg">D</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">DeepSeek</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  Cost-effective models with competitive performance on reasoning tasks.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">DeepSeek V3.2</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center shrink-0">
                <span className="text-cyan-500 font-bold text-lg">Z</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">Z.ai</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  GLM models with strong multilingual and multimodal capabilities.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">GLM-4.6V</span>
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">GLM-4.7</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
                <span className="text-purple-500 font-bold text-lg">M</span>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-zinc-200 mb-1">Moonshot</h4>
                <p className="text-sm text-zinc-500 mb-3">
                  Kimi models with native multimodal support and advanced thinking capabilities.
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-dark-surface text-zinc-400 text-xs rounded">Kimi K2.5</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Model Capabilities */}
      <section id="model-capabilities" className="mb-12 scroll-mt-20">
        <h2 className="text-xl sm:text-2xl font-semibold text-zinc-50 mb-4 flex items-center gap-3">
          <span className="text-green-500">03</span>
          Model Capabilities
        </h2>
        <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
          Each model has different strengths. The benchmark tests how these capabilities
          translate to trading performance.
        </p>

        <div className="space-y-4 mb-6">
          {models.map((model, idx) => (
            <div key={idx} className="bg-dark-tertiary border border-dark-border rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="font-semibold text-zinc-200">{model.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded ${
                      model.mode === 'vision'
                        ? 'bg-violet-500/10 text-violet-400'
                        : 'bg-green-500/10 text-green-400'
                    }`}>
                      {model.mode === 'vision' ? 'Vision' : 'Text'}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-500">{model.description}</p>
                </div>
                <div className="text-xs text-zinc-600 whitespace-nowrap">{model.provider}</div>
              </div>
            </div>
          ))}
        </div>

        <Callout type="info" title="Mode Selection">
          Models are assigned to text or vision mode based on their multimodal capabilities
          and optimal use case. Vision models analyze chart images directly, while text models
          process structured numerical data.
        </Callout>
      </section>

      {/* Related Pages */}
      <RelatedPages pages={seoConfig.relatedPages} />

      {/* Navigation Footer */}
      <div className="flex justify-between pt-8 border-t border-dark-border">
        <Link
          to="/docs/position-management"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <span>←</span>
          <div>
            <div className="text-[11px] text-zinc-600">Previous</div>
            <div className="text-zinc-50">Position Management</div>
          </div>
        </Link>
        <Link
          to="/docs/text-vs-vision"
          className="flex items-center gap-2 px-4 py-3 border border-dark-border rounded-lg
                     text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
        >
          <div className="text-right">
            <div className="text-[11px] text-zinc-600">Next</div>
            <div className="text-zinc-50">Text vs Vision Mode</div>
          </div>
          <span>→</span>
        </Link>
      </div>
    </DocsLayout>
  );
};

export default SupportedModels;
